import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Box, ListItem, ListItemIcon, ListItemText, MenuItem, MenuList, Stack, Theme, Typography } from "@mui/material";

import { Hub } from "aws-amplify/utils";
import { signOut } from "aws-amplify/auth";
import { useDispatch, useSelector } from "react-redux";
import { ReactRouterAppProvider } from "@toolpad/core/react-router";
import { Navigation, NavigationPageItem } from "@toolpad/core/AppProvider";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

import { Loader } from "@components/Loader";
import { RootState, store } from "@store/store";
import { PopupManager } from "@components/PopupManager";
import { DialogManager } from "@components/DialogManager";
import { setExpandStateLayoutSidebar } from "@store/slices/componentsSlice";
import {
    DashboardHeaderProps,
    DashboardLayout,
    DashboardSidebarPageItem,
    SidebarFooterProps,
} from "@toolpad/core/DashboardLayout";

import { PyMioTheme } from "@themes/PyMioTheme.ts";
import { FinbraTheme } from "@themes/FinbraTheme.ts";
import { KapitalFlexTheme } from "@themes/KapitalFlexTheme.ts";
import { BackOfficeTheme } from "@themes/BackOfficeTheme.ts";

import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { CustomHeader } from "@components/CustomHeader.tsx";
// import { AgentCCP } from "@components/CcpConnect";

export const SolicitudLayout = () => {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [themeToLoad, setThemeToLoad] = useState<Theme>(BackOfficeTheme);

    const sessionInfo = (state = store.getState()) => state.app.session;
    const expandLayoutSidebarState = (state = store.getState()) => state.components.layout.sidebar;

    const userSession = useSelector((state: RootState) => state.app.session.user);
    const permissions = useSelector((state: RootState) => state.app.session.permissions);
    const applicationLoaded = useSelector((state: RootState) => state.app.applicationSelected);
    const lockMenuApplication = useSelector((state: RootState) => state.components.menuApplication.lock);

    Hub.listen("auth", ({ payload }) => {
        switch (payload.event) {
            case "tokenRefresh_failure":
                dispatch({ type: "CLEAN_STORE" });

                navigate("/");
                break;
        }
    });

    const navigationOptions: Navigation = useMemo(() => {
        const dynamicPath = `solicitud/${params["companyName"]}/${params["id"]}`;
        return [
            {
                segment: "header",
                title: "algo",
            },
            {
                kind: "divider",
            },
            {
                title: "Info. General",
                segment: `${dynamicPath}`,
            },
            {
                title: "Perfil Financiero",
                segment: `${dynamicPath}/perfil_financiero`,
            },
            {
                segment: `${dynamicPath}/perfil_legal`,
                title: "Perfil Legal",
            },
            {
                segment: `${dynamicPath}/oferta`,
                title: "Oferta",
            },
            {
                kind: "divider",
            },
            {
                segment: `${dynamicPath}/dictamen_legal`,
                title: "Dictamen Legal",
            },
            {
                segment: `${dynamicPath}/expediente`,
                title: "Expediente",
            },
            {
                kind: "divider",
            },
            // {
            //     segment: `${dynamicPath}/validacion_crediticia`,
            //     title: "Validación Crediticia",
            // },
            {
                segment: `${dynamicPath}/riesgo_identidad`,
                title: "Riesgo e Identidad",
            },
            {
                segment: `${dynamicPath}/pld_integral`,
                title: "PLD Integral",
            },
            {
                kind: "divider",
            },
            {
                segment: `${dynamicPath}/visita_integral`,
                title: "Visita Ocular",
            },
            {
                kind: "divider",
            },
            {
                segment: `${dynamicPath}/revision_control`,
                title: "Revisión Control",
            },
            {
                kind: "divider",
            },
            {
                segment: `${dynamicPath}/contractuales`,
                title: "Contractuales",
            },
            {
                segment: `${dynamicPath}/cartera_dispersion`,
                title: "Cartera y Dispersión",
            },
            {
                segment: `${dynamicPath}/resumen`,
                title: "Resumen Operación",
            },
            {
                kind: "divider",
            },
        ];
    }, [location.pathname]);

    const disableByPermission = (text: string) => {
        switch (text) {
            case "Info. General":
                return !permissions.some((permission) => permission.module.code === "MIGR");

            case "Perfil Financiero":
                return !permissions.some((permission) => permission.module.code === "MPCR");

            case "Perfil Legal":
                return !permissions.some((permission) => permission.module.code === "MRPPL");

            case "Oferta":
                return !permissions.some((permission) => permission.module.code === "MOFER");

            case "Dictamen Legal":
                return !permissions.some((permission) => permission.module.code === "MDIC");

            case "Expediente":
                return !permissions.some((permission) => permission.module.code === "MEXE");

            // case "Validación Crediticia":
            //     return !permissions.some((permission) => permission.module.code === "MVCR");

            case "Riesgo e Identidad":
                return !permissions.some((permission) => permission.module.code === "MRIID");

            case "PLD Integral":
                return !permissions.some((permission) => permission.module.code === "MPLDI");

            case "Visita Ocular":
                return !permissions.some((permission) => permission.module.code === "MVOC");

            case "Revisión Control":
                return !permissions.some((permission) => permission.module.code === "MCCO");

            case "Contractuales":
                return !permissions.some((permission) => permission.module.code === "MCON");

            case "Cartera y Dispersión":
                return !permissions.some((permission) => permission.module.code === "MDIS");

            case "Resumen Operación":
                return !permissions.some((permission) => permission.module.code === "MRCR");
        }
    };

    const disableByProgress = (text: string) => {
        let step = 0;
        const step_status: Array<Array<string>> = [
            [
                "CSDUS",
                "CSRUS",
                "CSVTE",
                "CSDEM",
                "CSAEM",
                "CSUDC",
                "CSABA",
                "CSIGA",
                "CSPDI",
                "CSDAL",
                "CSCDA",
                "CSDAV",
                "CSCOF",
                "CSBBC",
                "CREVC",
                "CSREV",
                "CRECC",
                "CSREC",
                "CSRER",
                "CSCOM",
                "CSOFE",
                "CSEOF",
                "CRSO",
                "CSRES",
                "COFAC",
                "CSSSA",
                "CSREO",
                "CSLSA",
            ],
            ["CSAOL", "CSAAO", "CSOAC", "CSBEX", "CSDDI", "CSDIC", "CSAED", "CSEDC", "CSECI", "CSRDO"],
            ["CSVCC"],
            ["CSVCL", "CSAVC", "CSVDV", "CSVYC"],
            ["CSRIC", "CSRFO"],
            ["CSDDV", "CSEVD", "CSVDE", "CSBAF", "CSFIR", "CSFRL", "CSVDF", "CSDIS", "CSRCO"],
            ["CSFIE", "CSCLI"],
        ];

        const step_status_index = step_status.findIndex(
            (status) => status.some((st) => st === applicationLoaded.status_code) === true,
        );

        switch (text) {
            case "Info. General":
            case "Perfil Financiero":
            case "Perfil Legal":
            case "Oferta":
                step = 0;
                break;
            case "Dictamen Legal":
            case "Expediente":
                step = ["CSA01", "CSA02"].includes(applicationLoaded.tax_system.code) ? 1000 : 1;
                break;
            case "Validación Crediticia":
            case "Riesgo e Identidad":
            case "PLD Integral":
                step = ["CSA01", "CSA02"].includes(applicationLoaded.tax_system.code) ? 1000 : 2;
                break;
            case "Visita Ocular":
                step = 3;
                break;
            case "Revisión Control":
                step = ["CSA01", "CSA02"].includes(applicationLoaded.tax_system.code) ? 1000 : 4;
                break;
            case "Contractuales":
            case "Cartera y Dispersión":
                step = ["CSA01", "CSA02"].includes(applicationLoaded.tax_system.code) ? 1000 : 5;
                break;
            case "Resumen Operación":
                step = ["CSA01", "CSA02"].includes(applicationLoaded.tax_system.code) ? 1000 : 6;
                break;
        }

        // if (["CSA01", "CSA02"].includes(applicationLoaded.tax_system.code)) {
        //     return 1;
        // }

        return step > step_status_index;
    };

    const customHeader = (props: DashboardHeaderProps) => {
        return <CustomHeader {...props} />;
    };

    const sidebarFooter = (props: SidebarFooterProps) => {
        return (
            <Stack direction={"column"}>
                <MenuList>
                    <MenuItem
                        onClick={() =>
                            dispatch(
                                setExpandStateLayoutSidebar(!expandLayoutSidebarState(store.getState()).expandState),
                            )
                        }
                    >
                        <ListItemText>{props.mini ? "" : "Ocultar menú"}</ListItemText>
                        <ListItemIcon>{props.mini ? <MenuIcon /> : <MenuOpenIcon />}</ListItemIcon>
                    </MenuItem>
                </MenuList>
            </Stack>
        );
    };

    const handleLogout = async () => {
        dispatch({ type: "CLEAN_STORE" });
        await signOut();
        navigate("/");
    };

    const handleRenderPageItem = useCallback(
        (item: NavigationPageItem, { mini }: { mini: boolean }) => {
            if (item.segment?.startsWith("header")) {
                return (
                    <ListItem sx={{ justifyContent: "center", p: 0 }}>
                        <Typography component={"span"} variant="h5" fontWeight={300} color="main2">
                            {mini === false && `ID Solicitud: `}
                            <Typography component={"span"} variant="h5" fontWeight={600} color="main2">
                                {applicationLoaded.id_application_company?.toString().padStart(5, "0")}
                            </Typography>
                        </Typography>
                    </ListItem>
                );
            }

            return (
                <DashboardSidebarPageItem
                    item={item}
                    disabled={
                        location.pathname.startsWith("/admin")
                            ? false
                            : disableByProgress(item.title ?? "") ||
                              disableByPermission(item.title ?? "") ||
                              lockMenuApplication
                    }
                />
            );
        },
        [applicationLoaded.id, location.pathname, lockMenuApplication],
    );

    // useEffect(() => {
    //     dispatch(setCurrentSection(router.pathname));
    // }, [router]);

    useEffect(() => {
        if (sessionInfo(store.getState()).companies.length == 1) {
            if (sessionInfo(store.getState()).companies.at(0)?.name === "PYMIO") {
                setThemeToLoad(PyMioTheme);
            } else if (sessionInfo(store.getState()).companies.at(0)?.name === "FINBRA") {
                setThemeToLoad(FinbraTheme);
            } else if (sessionInfo(store.getState()).companies.at(0)?.name === "KAPITAL_FLEX") {
                setThemeToLoad(KapitalFlexTheme);
            } else {
                setThemeToLoad(BackOfficeTheme);
            }
        } else {
            if (applicationLoaded.company_source === "FINBRA") {
                setThemeToLoad(FinbraTheme);
            } else if (applicationLoaded.company_source === "KAPITAL_FLEX") {
                setThemeToLoad(KapitalFlexTheme);
            } else {
                setThemeToLoad(BackOfficeTheme);
            }
        }
    }, [sessionInfo(store.getState()).companies, applicationLoaded]);

    return (
        <ReactRouterAppProvider
            theme={themeToLoad}
            navigation={navigationOptions}
            session={{
                user: {
                    name: userSession.name?.concat(
                        " ",
                        userSession.last_name ?? "",
                        " ",
                        userSession.last_name_2 ?? "",
                    ),
                },
            }}
            authentication={{
                signOut: handleLogout,
                signIn: () => {},
            }}
        >
            <DashboardLayout
                renderPageItem={handleRenderPageItem}
                sidebarExpandedWidth={220}
                slots={{
                    sidebarFooter: sidebarFooter,
                    header: customHeader,
                }}
                sx={{ backgroundColor: "#f1f2f7" }}
            >
                <Suspense fallback={<Loader />}>
                    <Box py={3} px={4} height={"100vh"} id="content">
                        <Outlet />
                    </Box>
                    {/* <Box>
                        <AgentCCP />
                    </Box> */}
                </Suspense>
            </DashboardLayout>
            <DialogManager />
            <PopupManager />
        </ReactRouterAppProvider>
    );
};
